import {
  clampToZero,
  computeBucketDiscounts,
  fromIqd,
  subtract,
  ZERO_MD,
  type DayPassDto,
  type DeviceType,
  type Millidinars,
  type OpenTabSummary,
  type StartSessionInput,
} from '@gc/shared';

/**
 * The ReservationDrawer's wire payload and price preview, extracted so both
 * are testable in plain node (this workspace ships no DOM emulation, so a
 * submit can never be clicked in a test — the builder IS the seam).
 */

/** A registered account, as the drawer's customer search yields it. */
export interface SelectedCustomer {
  id: string;
  fullName: string;
  discountTimePercent: number;
}

/**
 * WHO this sitting belongs to — one of three things, never two.
 *
 * A union rather than three loose fields, because the loose version shipped a
 * money bug: the builder preferred `customerAccountId` while the server prefers
 * `tabId` (`sessions.service.ts` — the tab wins, unconditionally). A staff
 * member could pick a customer, then pick a tab, and watch the tab be dropped
 * on the way out — the sitting opened a NEW tab under the account instead of
 * joining the party, and that party's bill was short a machine until someone
 * noticed at the till.
 *
 * With one field holding one identity, the two cannot both be set, so whose
 * priority wins stops being a question anyone has to know the answer to.
 */
export type SittingIdentity =
  | { kind: 'walkIn'; name: string }
  | { kind: 'customer'; customer: SelectedCustomer }
  | { kind: 'tab'; tab: OpenTabSummary };

export interface StartSessionDraft {
  deviceId: string;
  identity: SittingIdentity;
  /** Used when a walk-in name is blank — the device's own name. */
  fallbackName: string;
  durationMinutes: string;
  freeMode: boolean;
  /**
   * Seat this sitting on a Boot Camp pass the party already bought (SPEC §6.1).
   *
   * Null is the ordinary case. When it is set the server snapshots a zero rate,
   * so the sitting bills nothing — the customer paid at the counter — and the
   * pass's own expiry becomes the sitting's planned end.
   */
  dayPassId?: string | null;
  /**
   * The Play button's courtesy sitting (owner, 2026-08-12): the server
   * snapshots a ZERO rate — no charge however long they play — and Outstanding
   * never lists it. Needs `discounts.apply`, the key the button hides behind.
   */
  complimentary?: boolean;
}

/**
 * Build the `POST /sessions` body. Exactly one identity field is emitted.
 *
 * `freeMode` goes over the wire EXPLICITLY — the schema treats "omitted" as
 * "whatever the device's flag says", and the drawer shows a checkbox, so what
 * is posted must be what the checkbox showed (the wave-3 fix for the phantom
 * toggle that was never sent at all).
 */
export function buildStartSessionPayload(draft: StartSessionDraft): StartSessionInput {
  const minutes = Number(draft.durationMinutes);

  return {
    deviceId: draft.deviceId,
    ...identityField(draft.identity, draft.fallbackName),
    ...(Number.isFinite(minutes) && minutes >= 1 ? { plannedMinutes: minutes } : {}),
    // A pass and free mode are refused together by the schema, and a pass is
    // the one the customer paid for — so seating on a pass never also claims
    // the venue comped it. The checkbox is disabled in the drawer to match.
    freeMode: draft.dayPassId ? false : draft.freeMode,
    ...(draft.dayPassId ? { dayPassId: draft.dayPassId } : {}),
    ...(draft.complimentary && !draft.dayPassId ? { complimentary: true } : {}),
  };
}

/** The one field on the wire that says who is playing. */
function identityField(
  identity: SittingIdentity,
  fallbackName: string,
): { tabId: string } | { customerAccountId: string } | { customerName: string } {
  switch (identity.kind) {
    case 'tab':
      return { tabId: identity.tab.id };
    case 'customer':
      return { customerAccountId: identity.customer.id };
    case 'walkIn':
      // A blank field is the commonest case on the board: staff press Save on
      // the defaults and the machine's own name becomes the party's.
      return { customerName: identity.name.trim() || fallbackName };
  }
}

/**
 * Put one more machine on a tab that already exists.
 *
 * Its own builder rather than a `SittingIdentity` because the caller has an id
 * and nothing else: when staff open five machines as one party, the first
 * machine CREATES the tab and the other four join it by the id that came back.
 * Faking an `OpenTabSummary` out of that id would put a name and a device count
 * into the type that nobody had measured.
 */
export function buildJoinTabPayload(input: {
  deviceId: string;
  tabId: string;
  durationMinutes: string;
  freeMode: boolean;
  /**
   * Only on the ONE machine the pass covers — see {@link passToCarry}. This
   * said "carried onto every machine of the party", and did exactly that.
   */
  dayPassId?: string | null;
}): StartSessionInput {
  const minutes = Number(input.durationMinutes);
  return {
    deviceId: input.deviceId,
    tabId: input.tabId,
    ...(Number.isFinite(minutes) && minutes >= 1 ? { plannedMinutes: minutes } : {}),
    freeMode: input.dayPassId ? false : input.freeMode,
    ...(input.dayPassId ? { dayPassId: input.dayPassId } : {}),
  };
}

/**
 * Re-exported, not defined here any more: the SERVER asks this question too.
 *
 * Guarding only the drawer left the other door open — see the note on the
 * shared definition. The rule now lives in `@gc/shared` so the screen that
 * refuses to attach an account and the clock that refuses to spend one cannot
 * drift apart.
 */
export { reservationUsesAccountBalance } from '@gc/shared';

/**
 * The account's time discount, as fed to the price preview: ALWAYS ZERO since
 * the cashback change. `discountTimePercent` became a refill cashback rate —
 * play credit granted when money is deposited — and the hourly rate is charged
 * in full at the till. Quoting the old half price here promised a total the
 * checkout no longer takes.
 *
 * Kept as a function (rather than inlining `0` at the call site) so the reason
 * lives next to the identity type it used to read.
 */
export function identityTimePercent(_identity: SittingIdentity): number {
  return 0;
}

/**
 * The live Boot Camp pass this party holds, if any (SPEC §6.1).
 *
 * Without this the pass is a receipt and nothing more: the server accepts a
 * `dayPassId` on `POST /sessions` and no screen has one to send, so a venue
 * could sell unlimited play and then have no way to seat anybody on it.
 *
 * A walk-in NAME can never match. The pass was sold onto a tab, and matching on
 * a typed name would seat a stranger on somebody else's pass the first time two
 * parties were both called "Ali" — staff pick that party's tab instead, which
 * is why a pass-only tab has to appear in the tab picker at all
 * (`SessionsService.listOpenTabs`).
 *
 * `isValid` is the server's own reading of the window, not a comparison made
 * here: a clock this browser disagrees with must never be what decides whether
 * a customer gets the hours they paid for.
 */
export function findPassForParty(
  passes: readonly DayPassDto[],
  identity: SittingIdentity,
): DayPassDto | null {
  if (identity.kind === 'walkIn') return null;

  const held = passes.filter((pass) => {
    if (!pass.isValid) return false;
    return identity.kind === 'tab'
      ? pass.tabId === identity.tab.id
      : pass.customerAccountId === identity.customer.id;
  });

  // Two live passes on one party is a staff double-sale, not a state to guess
  // at: take the one that runs longest, so the customer keeps the time they
  // were charged for and the other is visible on the Day Passes screen.
  return held.reduce<DayPassDto | null>(
    (best, pass) => (best === null || pass.validUntil > best.validUntil ? pass : best),
    null,
  );
}

/**
 * The pass id THIS machine's start should carry, or null.
 *
 * ONE PASS IS ONE SEAT (audit, 2026-09-23). The drawer found the party's pass
 * on its own and sent it on every machine it opened, so the one pass sold at
 * one PC's price made the whole party free — five friends on five PCs, the
 * cinema, the billiard table, all at once, with no way for staff to say no.
 * The server now refuses a second live sitting on a pass (`day_pass_in_use`);
 * this is the drawer agreeing with it:
 *
 *  - staff can DECLINE the pass (`usePass`), and then nothing carries it;
 *  - otherwise the first machine that STARTS on it takes it, and every machine
 *    after that is charged as usual. "Starts", not "is first in the list": a
 *    first machine that was refused (somebody got to it first) never used the
 *    pass, so the next one should.
 */
export function passToCarry(input: {
  partyPass: Pick<DayPassDto, 'id'> | null;
  usePass: boolean;
  /** An earlier machine in this same save already started on the pass. */
  alreadySeated: boolean;
}): string | null {
  if (!input.partyPass || !input.usePass || input.alreadySeated) return null;
  return input.partyPass.id;
}

export interface SessionPricePreview {
  /** The base charge before any discount (zero under free mode). */
  baseMd: Millidinars;
  /** The registered customer's time-bucket discount, via the shared math. */
  customerDiscountMd: Millidinars;
  /** Manual flat discount (whole IQD), as sent to `stop`'s `discountIqd`. */
  manualDiscountMd: Millidinars;
  totalMd: Millidinars;
}

/**
 * Price preview for the drawer.
 *
 * The percent part goes through {@link computeBucketDiscounts} — THE function
 * checkout charges with — so what the drawer promises a registered customer is
 * what the checkout till will take (the old drawer stacked its own ad-hoc
 * percent math and was wrong the moment checkout floored differently). Time is
 * the only bucket a device session can occupy; cafe and kitchen stay zero.
 */
export function previewSessionPrice(input: {
  baseMd: Millidinars;
  /**
   * The sitting is genuinely not charged for time — TODAY that means a Boot
   * Camp day pass, which snapshots a zero rate because it was paid for at the
   * counter.
   *
   * Renamed from `freeMode`, which is what it used to be fed. Free mode is a
   * SCREEN mode: the customer roams Windows rather than being held in the
   * portal, and the venue bills the sitting at its normal rate. Quoting zero
   * for it told staff a machine was free that the till would then charge for.
   */
  unbilledTime: boolean;
  /** `discountTimePercent` of the attached account; 0 when unknown/walk-in. */
  customerTimePercent: number;
  manualDiscountIqd: number;
}): SessionPricePreview {
  const baseMd = input.unbilledTime ? ZERO_MD : input.baseMd;

  const { totalDiscountMd: customerDiscountMd } = computeBucketDiscounts(
    { timeBaseMd: baseMd, cafeBaseMd: ZERO_MD, kitchenBaseMd: ZERO_MD },
    { timePercent: input.customerTimePercent, cafePercent: 0, kitchenPercent: 0 },
  );

  const manualDiscountMd = fromIqd(
    Number.isFinite(input.manualDiscountIqd) && input.manualDiscountIqd > 0
      ? Math.floor(input.manualDiscountIqd)
      : 0,
  );

  return {
    baseMd,
    customerDiscountMd,
    manualDiscountMd,
    totalMd: clampToZero(subtract(subtract(baseMd, customerDiscountMd), manualDiscountMd)),
  };
}
